<!-- This example requires Tailwind CSS v2.0+ -->
<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            class="fixed z-10 inset-0 overflow-y-auto"
            @close="doCloseModal()"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                    >&#8203;</span
                >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-4 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
                    >
                        <div class="mt-0">
                            <fieldset>
                                <legend
                                    class="text-lg font-medium text-gray-900"
                                >
                                    <svg
                                        class="animate-spin h-5 w-5 text-blue-900 float-right absolute right-0 mr-6 mt-2"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        v-if="loading"
                                    >
                                        <circle
                                            class="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            stroke-width="4"
                                        ></circle>
                                        <path
                                            class="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>

                                    <XIcon
                                        class="h-5 w-5 text-gray-400 float-right absolute right-0 mr-3 -mt-3 cursor-pointer hover:text-gray-900"
                                        @click="doCloseModal()"
                                    />

                                    {{
                                        t(
                                            "members.roles-modal.title-" + role,
                                            role
                                        )
                                    }}
                                </legend>
                                <p class="text-sm text-gray-500">
                                    {{
                                        t(
                                            "members.roles-modal.content-" +
                                                role,
                                            "Informatieve tekst bij " + role
                                        )
                                    }}
                                </p>
                                <div
                                    class="mt-4 border-t border-b border-gray-200 divide-y divide-gray-200"
                                >
                                    <div
                                        v-for="(role, id) in roles"
                                        :key="id"
                                        class="relative flex items-start py-3"
                                    >
                                        <div class="min-w-0 flex-1 text-sm">
                                            <label
                                                class="font-medium text-gray-700 select-none"
                                                >{{ role }}</label
                                            >
                                        </div>
                                        <div
                                            class="ml-3 flex items-center h-5"
                                            @click="deleteRole(id)"
                                        >
                                            <TrashIcon
                                                class="h-4 w-4 text-gray-500 inline float-right -mt-1 hover:text-red-900 cursor-pointer"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            <div class="mt-4">
                                <div class="mt-2 flex rounded-md shadow-sm">
                                    <div
                                        class="relative flex items-stretch flex-grow focus-within:z-10"
                                    >
                                        <input
                                            type="text"
                                            name="role"
                                            v-model="input"
                                            id="role"
                                            class="block w-full rounded-none rounded-l-md sm:text-sm border-gray-300 focus:outline-none"
                                            :class="{
                                                'border-red-400': error == 422,
                                            }"
                                            :placeholder="placeholderInput()"
                                        />
                                    </div>
                                    <button
                                        @click="addRole()"
                                        class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                                    >
                                        <PlusIcon
                                            class="h-5 w-5 text-gray-600"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    TagIcon,
    UserGroupIcon,
    PlusIcon,
    TrashIcon,
    XIcon,
} from "@heroicons/vue/outline";

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        TagIcon,
        UserGroupIcon,
        PlusIcon,
        TrashIcon,
        XIcon,
    },
    mounted() {
        Eventing.listen("closeModal", () => (this.open = false));
        Eventing.listen("fetchRoles", () => this.getRoles());
        this.getRoles();
    },
    created() {},
    props: ["role"],
    data() {
        const open = ref(true);

        return {
            open,
            roles: {},
            loading: true,
            input: null,
            error: null,
        };
    },
    methods: {
        placeholderInput() {
            return this.t(
                "members.roles-modal.placeholder-" + this.role,
                "Voeg rol toe " + this.role
            );
        },
        getRoles() {
            this.loading = true;
            axios
                .get("/api/modules/roles")
                .then((response) => {
                    if (this.role == "members") {
                        this.roles = response.data.membertypes;
                    }
                    if (this.role == "groups") {
                        this.roles = response.data.groups;
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    alert(error.response.status);
                    // this.loading = "error";
                });
        },
        addRole() {
            axios
                .post("/api/modules/roles", {
                    name: this.input,
                    group:
                        this.role.charAt(0).toUpperCase() + this.role.slice(1),
                })
                .then((response) => {
                    this.input = null;
                    this.getRoles();
                    Eventing.fire("fetchRoles");
                })
                .catch((error) => {
                    if (error.response) {
                        console.warn(error.response.data);
                        this.error = error.response.status;
                    }
                });
        },
        deleteRole(id) {
            var _confirm = this.t(
                "members.roles-modal.confirm-delete-" + this.role,
                "Wissen?"
            );
            if (confirm(_confirm)) {
                this.loading = true;
                axios
                    .delete("/api/modules/roles/" + id)
                    .then((response) => {
                        this.getRoles();
                        Eventing.fire("fetchRoles");
                    })
                    .catch((error) => {
                        alert(error.response.status);
                        // this.error = error.response.status;
                    });
            }
        },
        doCloseModal() {
            Eventing.fire("closeModal");
        },
    },
};
</script>
